import {
  SourcingPerformanceCriteria,
  SourcingPerformanceStatus,
} from 'api-clients/monolith';
import _round from 'lodash/round';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import BlueDot from '@/assets/icons/blue-dot.svg';
import GreenDot from '@/assets/icons/green-dot.svg';
import GreyDot from '@/assets/icons/grey-dot.svg';
import RedDot from '@/assets/icons/red-dot.svg';

interface UsePerformanceStatusProps {
  status: SourcingPerformanceStatus;
  criteria: SourcingPerformanceCriteria;
  expected: number;
  actual: number;
}

export const usePerformanceStatus = ({
  status,
  criteria,
  expected,
  actual,
}: UsePerformanceStatusProps) => {
  const intl = useIntl();

  const percentage = Math.abs(_round(((expected - actual) / expected) * 100));
  const trend = expected > actual ? 'lower' : 'higher';
  const type = criteria === 'hire' ? 'hires' : 'applicants';

  return useMemo(() => {
    switch (status) {
      case 'goal_met':
        return {
          bgColor: 'bg-fx-green-50',
          icon: GreenDot,
          tooltip: intl.formatMessage(
            {
              defaultMessage:
                'Hurray! The target was met. There are {actual} {type} out of a goal of {expected}.',
              description:
                'Message shown when the performance goal is met. {actual} is the achieved number, {type} is either "hires" or "applicants", and {expected} is the goal number.',
            },
            {
              type,
              actual,
              expected,
            },
          ),
        };
      case 'on_track':
        return {
          bgColor: 'bg-fx-blue-50',
          icon: BlueDot,
          tooltip:
            expected === actual ? (
              <>
                <FormattedMessage
                  defaultMessage="You are right on track — {actual} {type} so far, matching the expected {expected} {type} by today."
                  description='Message shown when the performance goal is on track. {type} is either "hires" or "applicants", {expected} is the goal number, and {actual} is the achieved number.'
                  values={{ type, expected, actual }}
                />
              </>
            ) : (
              <>
                <FormattedMessage
                  defaultMessage="The number of {type} is {percentage}% {trend} than the expected {type} by today."
                  description='Message shown when the performance goal is on track. {type} is either "hires" or "applicants", {percentage} is the percentage difference, and {trend} is either "lower" or "higher".'
                  values={{ type, percentage, trend }}
                />
                <br />
                <br />
                <FormattedMessage
                  defaultMessage="To hit the goal, there should have been {expected} {type} by today, but there are {actual} {type} already."
                  description='Message shown when the performance goal is on track. {type} is either "hires" or "applicants", {expected} is the goal number, and {actual} is the achieved number.'
                  values={{ type, expected, actual }}
                />
              </>
            ),
        };
      case 'at_risk':
        return {
          bgColor: 'bg-fx-rose-50',
          icon: RedDot,
          tooltip: (
            <>
              <FormattedMessage
                defaultMessage="The number of {type} is {percentage}% {trend} than the expected {type} by today."
                description='Message shown when the performance goal is at risk. {type} is either "hires" or "applicants", {percentage} is the percentage difference, and {trend} is either "lower" or "higher".'
                values={{ type, percentage, trend }}
              />
              <br />
              <br />
              <FormattedMessage
                defaultMessage="To hit the goal, there should have been {expected} {type} by today, but there are only {actual} {type}."
                description='Message shown when the performance goal is at risk. {type} is either "hires" or "applicants", {expected} is the goal number, and {actual} is the achieved number.'
                values={{ type, expected, actual }}
              />
            </>
          ),
        };
      case 'not_started':
      case 'no_data':
      default:
        return {
          bgColor: 'bg-fx-neutral-100',
          icon: GreyDot,
          tooltip: intl.formatMessage({
            defaultMessage:
              'There is not enough data available at this time to accurately assess progress',
            description:
              'Message shown when there is not enough data available to accurately assess progress.',
          }),
        };
    }
  }, [status, intl, expected, actual, type, percentage, trend]);
};
